import { useAuth } from '@teko/auth-react';
import {
  ChecklistOutlined,
  LocationOutlined,
  OrderOutlined,
} from '@teko/ds-icon';
import { Body, Col, CustomLink, Flex } from '@teko/ds-desktop';
import { getTrackingData } from '@teko/tracking';
import { UserProfileHeader } from 'components/desktop/organisms';
import { NavigationButton } from 'components/desktop/organisms/style';
import { RegionPickerSuggestionPopover } from 'components/RegionPickerSuggestionPopover';
import {
  useFeatureFlags,
  useGlobalData,
  useLocation,
  useLoyalty,
} from 'contexts';
import { getNavItems, isServerless } from 'helpers';
import { useRegionPicker } from 'hooks';
import { t } from 'i18n';
import { CartOverview } from 'modules/cart';
import { NotificationOverview } from 'modules/notification';
import { SlotComponentProps } from 'modules/page-builder';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import {
  defaultNavItemDisplayConfig,
  isVerticalNavItem,
  NavItem,
} from 'tenants/_template/desktop/_module/components/NavigationBar';

export type HeaderRightItemProps = SlotComponentProps<{}>;

export const HeaderRightItemDesktop: React.FC<HeaderRightItemProps> = ({}) => {
  const {} = useRouter();
  const {
    partner,
    tenant,

    notificationAppId,
  } = useGlobalData();
  const { user, authService } = useAuth();
  const { loyaltyMemberInfo } = useLoyalty();
  const { flags } = useFeatureFlags();
  const regionPickerProps = useRegionPicker();
  const region = regionPickerProps?.activeLocation;

  const {
    setShowed: showLocationPopup,
    selectedLocation,
    showRegionPickerSuggestionPopover,
    onCloseRegionPickerSuggestionPopover,
    enabled: locationPickerEnabled,
  } = useLocation();
  const onLogin = () => authService.login(window.location.href);
  const router = useRouter();

  const profileLinks = useMemo(() => {
    return getNavItems({
      tenant,
      logged: !!user?.userId,
      isLoyaltyMember: !!loyaltyMemberInfo,
      showNotificationItem: !!notificationAppId,
    });
  }, [tenant, user]);

  const RegionPickerIcon = ({ locationName }: { locationName?: string }) => {
    const showRegionPickerModal = () => {
      if (flags.epic_ecom_284) {
        showLocationPopup(true);
      } else {
        regionPickerProps.onOpenPopup();
      }
    };

    // Don't show this icon on the server side
    if (!isServerless()) {
      return null;
    }

    return (
      <RegionPickerSuggestionPopover
        active={showRegionPickerSuggestionPopover}
        onClose={onCloseRegionPickerSuggestionPopover}
      >
        <NavItem
          onClick={showRegionPickerModal}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'location',
          })}
          className="att-home-page-delivery-area"
        >
          <LocationOutlined size={36} color="textSecondary" />
          <Body level={2} weight="medium" color="textSecondary">
            {locationName}
          </Body>
        </NavItem>
      </RegionPickerSuggestionPopover>
    );
  };

  const navItems = useMemo(() => {
    const { order, login, cart, additional } = defaultNavItemDisplayConfig;
    const navMenu = [];
    if (order)
      navMenu.push(
        <NavigationButton
          className="button"
          onClick={(): void => {
            if (!user?.userId) {
              onLogin();
            } else {
              router.push('/account/orders');
            }
          }}
          style={
            isVerticalNavItem(tenant)
              ? { flexDirection: 'column' }
              : { flexDirection: 'row' }
          }
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'headerBar',
            contentName: 'orderIcon',
          })}
        >
          <OrderOutlined size={36} color="textSecondary" />
          <span className="title">{t('Đơn hàng')}</span>
        </NavigationButton>,
      );
    if (login)
      navMenu.push(
        <UserProfileHeader
          vertical={isVerticalNavItem(tenant)}
          customProfile={
            !!partner ? (
              <CustomLink href="/account/orders">
                <NavigationButton className="button">
                  <ChecklistOutlined size={36} color="textSecondary" />
                  <div>{t('Quản lý đơn hàng')}</div>
                </NavigationButton>
              </CustomLink>
            ) : undefined
          }
          profileLinks={profileLinks}
        />,
      );
    if (notificationAppId)
      navMenu.push(<NotificationOverview title={isVerticalNavItem(tenant)} />);
    if (cart)
      navMenu.push(
        <CartOverview
          showShippingFeeMiniCart={true}
          vertical={isVerticalNavItem(tenant)}
        />,
      );
    if (locationPickerEnabled) {
      navMenu.push(
        <RegionPickerIcon locationName={selectedLocation?.locationName} />,
      );
    } else if (region && regionPickerProps?.terminals?.length > 1) {
      navMenu.push(<RegionPickerIcon locationName={region?.label} />);
    }
    if (additional) navMenu.push(additional);
    return navMenu;
  }, [
    locationPickerEnabled,
    notificationAppId,
    partner,
    profileLinks,
    region,
    selectedLocation?.locationName,
    tenant,
    user?.userId,
  ]);
  return (
    <Flex flexWrap="nowrap" gap="s16" align="center" justify="end">
      {navItems.map((item, index) => (
        <Col key={index}>{item}</Col>
      ))}
    </Flex>
  );
};
